import React, {
  useEffect, useMemo, useRef, useState, Fragment,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Scrollbars } from 'react-custom-scrollbars';
import {
  CButton,
} from '@coreui/react';

import { fetchSmsMessages, sendSmsMessage } from './actions';
import Message from './Message';
import { resetScrollToBottom, resetChat } from './actionCreators';
import gsmMessage from './gsmMessage';

import './styles.scss';

const propTypes = {
  chatID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recruitName: PropTypes.string.isRequired,
};

const defaultProps = {
  chatID: null,
};

const ChatComponent = ({ chatID, recruitName }) => {
  const [inputValue, setInputValue] = useState('');
  const scrollbarRef = useRef(null);
  const dispatch = useDispatch();
  const {
    messages,
    initialChat,
    sendingMessage,
    forceScrollToBottom,
  } = useSelector((state) => state.recruits.chat);
  // const { id: userID } = useSelector(({ account }) => account.user);

  const fetchChatMessages = () => dispatch(fetchSmsMessages(chatID));

  useEffect(() => () => {
    dispatch(resetChat());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialChat) setTimeout(() => fetchChatMessages(), 0);
    // eslint-disable-next-line
  }, [initialChat]);

  useEffect(() => {
    const { scrollHeight, scrollTop, clientHeight } = scrollbarRef.current.getValues();
    if (forceScrollToBottom && scrollHeight - (scrollTop + clientHeight) < 200) {
      dispatch(resetScrollToBottom());
      scrollbarRef.current.scrollToBottom();
    }
    // eslint-disable-next-line
  }, [forceScrollToBottom]);

  useEffect(() => {
    if (!initialChat) scrollbarRef.current.scrollToBottom();
  }, [initialChat]);

  const sendMessage = async () => {
    if (!sendingMessage) {
      dispatch(sendSmsMessage(chatID, inputValue.trim()));
      setInputValue('');
    }
  };

  const onInputChange = (e) => setInputValue(gsmMessage(e.target.value));

  const onInputKeyDown = (e) => {
    if (e.keyCode === 13 && inputValue.trim()) {
      sendMessage();
    }
  };

  const renderMessages = useMemo(() => {
    if (initialChat) return null;
    if (messages.length) {
      return (
        <div className="live-chat__messages-list">
          {messages.map((msg) => {
            const isMine = msg.type === 'sent';
            const showAuthor = !isMine;
            return (
              <Fragment key={`message_${msg?.id}`}>
                <Message {...msg} isMine={isMine} />
                {showAuthor && (
                  <div className="live-chat__message--author">
                    {recruitName}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      );
    }
    return null;
    // eslint-disable-next-line
  }, [initialChat, JSON.stringify(messages)]);

  return (
    <>
      <section className="live-chat__wrapper">
        <Scrollbars hideTracksWhenNotNeeded ref={scrollbarRef}>
          {renderMessages}
        </Scrollbars>
      </section>
      <div className="live-chat__new-message">
        <input
          type="text"
          className="live-chat__new-message--input"
          placeholder="Type your SMS message here..."
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          name="message-input"
        />
        <CButton
          disabled={initialChat || !inputValue.trim() || sendingMessage}
          onClick={sendMessage}
          color="primary"
          data-test-id="send-message"
        >
          Send SMS
        </CButton>
      </div>
    </>
  );
};

ChatComponent.propTypes = propTypes;
ChatComponent.defaultProps = defaultProps;

export default ChatComponent;
